import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { api } from '../api/client';
import { useLang } from '../i18n/lang';
import {
  getPageCount,
  extractLegendImages,
  type ExtractResult,
  type SymbolForMatching,
} from '../lib/legendCrops';

// Legend → Symbol Images screen. MyKyah's approach: use pdf.js text positions
// to find each symbol's label in the PDF, crop the actual glyph to its LEFT.
// No column dividers, no AI bbox guessing — the PDF text layer has exact
// positions, we use them directly.

export function LegendLayout() {
  const { t } = useLang();
  const { id = '' } = useParams();
  const [file, setFile] = useState<File | null>(null);
  const [pageCount, setPageCount] = useState(0);
  const [pageNum, setPageNum] = useState(1);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState('');
  const [result, setResult] = useState<ExtractResult | null>(null);
  const [symbols, setSymbols] = useState<SymbolForMatching[]>([]);
  const [error, setError] = useState('');

  useEffect(() => {
    api.getAnalysis(id).then((a) => {
      setSymbols(
        a.symbols.map((s) => ({
          id: s.id,
          symbol_tag: s.symbol_tag,
          legend_description: s.legend_description,
        })),
      );
    }).catch((e) => setError(e instanceof Error ? e.message : t({ en: 'Failed to load symbols', fil: 'Hindi na-load ang mga symbol' })));
  }, [id]);

  async function onFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    setError(''); setResult(null);
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    try {
      const n = await getPageCount(f);
      setPageCount(n);
      setPageNum(1);
    } catch (err) {
      setError(err instanceof Error ? err.message : t({ en: 'Could not read PDF', fil: 'Hindi mabasa ang PDF' }));
    }
  }

  async function extract() {
    if (!file) { setError(t({ en: 'Choose the plan PDF first.', fil: 'Piliin muna ang plan PDF.' })); return; }
    if (!symbols.length) { setError(t({ en: 'No symbols on this project yet — run Analyze first.', fil: 'Wala pang symbols sa project na ito — i-run muna ang Analyze.' })); return; }
    setBusy(true); setError(''); setResult(null);
    try {
      const r = await extractLegendImages(file, pageNum, id, symbols, (p) => {
        setStatus(p.message ?? `${p.stage} ${p.done}/${p.total}`);
      });
      setResult(r);
      setStatus(t({ en: `Done. ${r.matched} symbol image(s) attached.`, fil: `Tapos na. ${r.matched} symbol image(s) ang naka-attach.` }));
    } catch (e) {
      setError(e instanceof Error ? e.message : t({ en: 'Extraction failed', fil: 'Nabigo ang extraction' }));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div>
      <Link to={`/projects/${id}`} className="muted">← {t({ en: 'Back to project', fil: 'Bumalik sa project' })}</Link>
      <h1 style={{ marginTop: 4 }}>{t({ en: 'Get Symbol Images from Legend', fil: 'Kunin ang Symbol Images mula sa Legend' })}</h1>
      <p className="muted" style={{ maxWidth: 760 }}>
        {t({
          en: "Re-upload the plan PDF. The app reads each symbol's label position straight from the PDF's text layer, then crops the actual symbol from the page. The cropped images are embedded in your Count Sheet's Symbol column.",
          fil: "I-upload muli ang plan PDF. Binabasa ng app ang label position ng bawat symbol mismo sa text layer ng PDF, tapos kino-crop ang aktwal na symbol mula sa page. Ang mga na-crop na image ay ineembed sa Symbol column ng iyong Count Sheet.",
        })}
      </p>

      <div className="card" style={{ marginBottom: 12 }}>
        <div className="field" style={{ marginBottom: 10 }}>
          <label>{t({ en: 'Plans PDF', fil: 'Plans PDF' })}</label>
          <input type="file" accept="application/pdf" onChange={onFileChange} disabled={busy} />
          <small className="muted">
            {t({
              en: "The file is read in your browser — it isn't re-uploaded for AI analysis. Needs a text layer (most modern construction PDFs have one).",
              fil: 'Binabasa ang file sa iyong browser — hindi ito muling ina-upload para sa AI analysis. Kailangan ng text layer (karamihan ng modernong construction PDFs ay mayroon nito).',
            })}
          </small>
        </div>
        {pageCount > 0 && (
          <div className="field" style={{ marginBottom: 10 }}>
            <label>{t({ en: `Legend page (of ${pageCount})`, fil: `Legend page (sa ${pageCount})` })}</label>
            <input type="number" min={1} max={pageCount} value={pageNum} onChange={(e) => setPageNum(Math.max(1, Math.min(pageCount, +e.target.value || 1)))} style={{ width: 80 }} />
            <small className="muted">{t({ en: 'Open your PDF and find the "Symbols, Legends and Abbreviations" sheet (often the first T-sheet).', fil: 'Buksan ang iyong PDF at hanapin ang "Symbols, Legends and Abbreviations" sheet (madalas ang unang T-sheet).' })}</small>
          </div>
        )}
        {file && (
          <button className="btn btn-primary" onClick={extract} disabled={busy || !symbols.length}>
            {busy ? t({ en: 'Extracting…', fil: 'Ine-extract…' }) : t({ en: `Extract symbol images (${symbols.length} symbols)`, fil: `I-extract ang symbol images (${symbols.length} symbols)` })}
          </button>
        )}
        {status && <p className="muted" style={{ marginTop: 8, marginBottom: 0 }}>{status}</p>}
        {error && <div className="error">{error}</div>}
      </div>

      {result && (
        <div className="card">
          <h2 style={{ marginTop: 0 }}>{t({ en: 'Result', fil: 'Resulta' })}</h2>
          <p>
            <strong>{result.matched}</strong> {t({ en: 'of', fil: 'sa' })} <strong>{symbols.length}</strong> {t({ en: 'symbol image(s) attached.', fil: 'symbol image(s) ang naka-attach.' })}
          </p>
          {result.unmatched_symbols.length > 0 && (
            <details>
              <summary>{t({ en: `${result.unmatched_symbols.length} symbol(s) whose label wasn't found in the PDF text`, fil: `${result.unmatched_symbols.length} symbol(s) na ang label ay hindi nakita sa text ng PDF` })}</summary>
              <ul style={{ fontSize: 13, color: 'var(--gray-500)' }}>
                {result.unmatched_symbols.map((l, i) => <li key={i}>{l}</li>)}
              </ul>
            </details>
          )}
          {result.white_patches.length > 0 && (
            <details>
              <summary>{t({ en: `${result.white_patches.length} label(s) with no symbol visible at the expected position`, fil: `${result.white_patches.length} label(s) na walang nakikitang symbol sa inaasahang posisyon` })}</summary>
              <ul style={{ fontSize: 13, color: 'var(--gray-500)' }}>
                {result.white_patches.map((l, i) => <li key={i}>{l}</li>)}
              </ul>
            </details>
          )}
          {result.errors.length > 0 && (
            <details>
              <summary>{t({ en: `${result.errors.length} error(s)`, fil: `${result.errors.length} (na) error` })}</summary>
              <ul style={{ fontSize: 13, color: 'var(--danger)' }}>
                {result.errors.map((er, i) => <li key={i}>{er}</li>)}
              </ul>
            </details>
          )}
          <p style={{ marginTop: 12 }}>
            <Link to={`/projects/${id}/count`} className="btn btn-primary">{t({ en: 'Continue to Count Sheet', fil: 'Magpatuloy sa Count Sheet' })} →</Link>
          </p>
        </div>
      )}
    </div>
  );
}
